import React from 'react';
import { Plus, Minus, Info } from 'lucide-react';
import { sanitiseAmountInput, inr } from '../../utils/billing';
import { NOTE_MAX } from '../../utils/bookingValidation';

/**
 * The two ways an invoice can be adjusted, kept deliberately distinct:
 *
 *   Extra charges  — money ADDED (laundry, late checkout, damages). Taxed with the room.
 *   Discount       — money DEDUCTED (concessions, waivers). Applied after tax.
 *
 * Both accept positive numbers only. Staff had been typing negative extra charges to give
 * discounts, which quietly corrupted the GST base and produced invoices whose tax did not
 * match their total. Making the deduction its own clearly-labelled field is what removes
 * the incentive to do that.
 */
const ChargeAdjustments = ({
  extraCharges,
  extraChargesNote,
  discount,
  discountNote,
  onChange,
  maxDiscount,
  errors = {},
  disabled = false
}) => {
  const set = (patch) => onChange({ extraCharges, extraChargesNote, discount, discountNote, ...patch });

  const discountValue = parseFloat(discount) || 0;
  const exceedsMax = maxDiscount !== undefined && discountValue > maxDiscount;

  const inputClass = (hasError) =>
    `w-full pl-9 pr-3 py-2.5 bg-white border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
      hasError
        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
        : 'border-midnight-200 focus:border-railway-500 focus:ring-railway-500/20'
    }`;

  return (
    <div className="space-y-4">
      {/* Extra charges — added */}
      <div className="rounded-xl border border-orange-200 bg-orange-50/50 p-3.5">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-6 h-6 rounded-md bg-orange-500 text-white flex items-center justify-center">
            <Plus className="w-3.5 h-3.5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-orange-900">Extra Charges</p>
            <p className="text-xs text-orange-700">Added to the invoice — laundry, damages, late checkout</p>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-midnight-400 font-medium">₹</span>
            <input
              type="text"
              inputMode="decimal"
              value={extraCharges}
              onChange={(e) => set({ extraCharges: sanitiseAmountInput(e.target.value) })}
              disabled={disabled}
              placeholder="0"
              aria-label="Extra charges amount"
              className={inputClass(errors.extraCharges)}
            />
          </div>
          <input
            type="text"
            value={extraChargesNote}
            onChange={(e) => set({ extraChargesNote: e.target.value.slice(0, NOTE_MAX) })}
            disabled={disabled}
            maxLength={NOTE_MAX}
            placeholder="Reason (e.g. Laundry)"
            aria-label="Extra charges reason"
            className="w-full px-3 py-2.5 bg-white border-2 border-midnight-200 rounded-lg focus:outline-none focus:border-railway-500 focus:ring-2 focus:ring-railway-500/20 transition-all"
          />
        </div>
        {errors.extraCharges && <p className="mt-1.5 text-xs text-red-600">{errors.extraCharges}</p>}
      </div>

      {/* Discount — deducted */}
      <div className="rounded-xl border border-green-300 bg-green-50/60 p-3.5">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-6 h-6 rounded-md bg-green-600 text-white flex items-center justify-center">
            <Minus className="w-3.5 h-3.5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-green-900">Discount / Concession</p>
            <p className="text-xs text-green-800">Deducted from the invoice total</p>
          </div>
        </div>

        <div className="flex items-start gap-2 mb-2.5 rounded-lg bg-green-100/70 px-2.5 py-2 text-xs text-green-900">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>
            Whatever you enter here is <strong>subtracted</strong> from the guest's bill. Enter it
            as a positive number — never type a minus sign, and never use Extra Charges to reduce a bill.
          </span>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-700 font-medium">− ₹</span>
            <input
              type="text"
              inputMode="decimal"
              value={discount}
              onChange={(e) => set({ discount: sanitiseAmountInput(e.target.value) })}
              disabled={disabled}
              placeholder="0"
              aria-label="Discount amount"
              className={`w-full pl-11 pr-3 py-2.5 bg-white border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                errors.discount || exceedsMax
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-green-300 focus:border-green-500 focus:ring-green-500/20'
              }`}
            />
          </div>
          <input
            type="text"
            value={discountNote}
            onChange={(e) => set({ discountNote: e.target.value.slice(0, NOTE_MAX) })}
            disabled={disabled}
            maxLength={NOTE_MAX}
            placeholder="Reason (e.g. Senior citizen concession)"
            aria-label="Discount reason"
            className="w-full px-3 py-2.5 bg-white border-2 border-midnight-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all"
          />
        </div>

        {(errors.discount || exceedsMax) && (
          <p className="mt-1.5 text-xs text-red-600">
            {errors.discount || `Discount cannot exceed the bill total of ${inr(maxDiscount)}`}
          </p>
        )}
        {!errors.discount && !exceedsMax && discountValue > 0 && !String(discountNote || '').trim() && (
          <p className="mt-1.5 text-xs text-amber-700">
            Add a reason — discounts without one are hard to justify during an audit.
          </p>
        )}
      </div>
    </div>
  );
};

export default ChargeAdjustments;
